"use client";

import { LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface Benefit {
  title: string;
  body: string;
  icon?: LucideIcon;
  accent: string;
}

export interface BenefitsSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  benefits: Benefit[];
  accent: string;
  gridCols?: 2 | 3 | 4;
}

export function BenefitsSection({
  id = "benefits",
  eyebrow,
  title,
  benefits,
  accent,
  gridCols = 4,
}: BenefitsSectionProps) {
  return (
    <Section id={id} className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute top-1/4 right-1/4 h-80 w-80 rounded-full opacity-15 blur-[120px]"
          style={{ background: accent }}
        />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: accent }}>
              {eyebrow}
            </span>
            <h2 className="mt-3">{title}</h2>
          </div>
        </Reveal>

        <div className={`mt-12 grid gap-4 ${gridCols === 2 ? "sm:grid-cols-2" : gridCols === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-4"}`}>
          {benefits.map((b, i) => {
            const Icon = b.icon;
            const bAccent = b.accent;
            return (
              <Reveal key={b.title} delay={i * 60}>
                <div
                  className="glass rounded-2xl p-5 h-full"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${bAccent} 12%, transparent)`,
                  }}
                >
                  {Icon ? (
                    <span
                      className="grid place-items-center h-10 w-10 rounded-lg"
                      style={{
                        background: `color-mix(in oklch, ${bAccent} 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${bAccent} 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-4 w-4" style={{ color: bAccent }} />
                    </span>
                  ) : null}
                  <h3 className="mt-3 text-base">{b.title}</h3>
                  <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{b.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}
